import React, { useMemo } from 'react';
import { Card, Row, Col, Tag, Typography, Divider } from 'antd';

const { Text } = Typography;

// Each bucket defines the metrics that feed into its score.
// weights are relative — only the ratio matters; missing-data metrics are excluded and weights renormalized.
const BUCKETS = [
  {
    key: 'call_handling',
    label: 'Call Handling',
    metrics: [
      { key: 'AvgHTSecs',       label: 'Inbound Avg Handle Time',  source: 'campaign', weight: 1 },
    ],
  },
  {
    key: 'quality',
    label: 'Quality',
    metrics: [
      // One QA score. CXOne scores were spliced into AvgQAScore for months
      // through 2026-09, so a separate CXOne metric would count the same
      // number twice for those months and show an empty row for every month
      // since.
      { key: 'AvgQAScore',      label: 'QA Score',         source: 'campaign', weight: 0.25 },
      { key: 'ErrorCount',      label: 'Errors',           source: 'campaign', weight: 0.20 },
      { key: 'RecoachingCount', label: 'Recoach Sessions', source: 'overall',  weight: 0.15 },
    ],
  },
  {
    key: 'adherence',
    label: 'Schedule Adherence',
    metrics: [
      { key: 'AdherencePct',         label: 'Adherence %',           source: 'overall', weight: 0.6 },
      { key: 'ShortTermNoticeCount', label: 'Absence Notifications', source: 'overall', weight: 0.4 },
    ],
  },
  {
    key: 'ccaas',
    label: 'Compliance',
    metrics: [
      { key: 'ComplianceCount',      label: 'Compliance Incidents', source: 'overall', weight: 0.70 },
      { key: 'CCaaSUnproductivePct', label: 'Unproductive %',       source: 'overall', weight: 0.15 },
      { key: 'CCaaSUnavailablePct',  label: 'Unavailable %',        source: 'overall', weight: 0.15 },
    ],
  },
];

const GRADE_THRESHOLDS = [
  { min: 80, letter: 'A', color: 'success' },
  { min: 65, letter: 'B', color: 'processing' },
  { min: 50, letter: 'C', color: 'warning' },
  { min: 35, letter: 'D', color: 'orange' },
  { min: 0,  letter: 'F', color: 'error' },
];

function getLetterGrade(score) {
  if (score == null) return null;
  return GRADE_THRESHOLDS.find(t => score >= t.min) || null;
}

function ordinalSuffix(n) {
  if (n == null) return null;
  const abs = Math.abs(n);
  const mod100 = abs % 100;
  const mod10  = abs % 10;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  if (mod10 === 1) return `${n}st`;
  if (mod10 === 2) return `${n}nd`;
  if (mod10 === 3) return `${n}rd`;
  return `${n}th`;
}

// Compute a weighted average percentile for a bucket, dropping metrics with no data.
function computeBucketScore(bucket, overallMap, campaignRows) {
  let weightedSum = 0;
  let totalWeight = 0;
  const metricResults = [];

  for (const m of bucket.metrics) {
    let pct = null;
    let rank = null;
    let n = null;
    if (m.source === 'overall') {
      const row = overallMap[m.key];
      pct  = row?.percentile ?? null;
      rank = row?.rank       ?? null;
      n    = row?.n          ?? null;
    } else {
      const rows = campaignRows.filter(r => r.metric === m.key && r.percentile != null);
      if (rows.length) {
        pct = rows.reduce((s, r) => s + r.percentile, 0) / rows.length;
        if (rows.length === 1) { rank = rows[0].rank ?? null; n = rows[0].n ?? null; }
      }
    }
    const rounded = pct != null ? Math.round(pct) : null;
    metricResults.push({ ...m, percentile: rounded, rank, n });

    if (rounded != null) {
      weightedSum += rounded * m.weight;
      totalWeight += m.weight;
    }
  }

  const score = totalWeight > 0 ? Math.round(weightedSum / totalWeight) : null;
  return { score, metrics: metricResults };
}

export default function AgentGradeCard({ overall = [], campaign = [] }) {
  const overallMap = useMemo(
    () => Object.fromEntries(overall.map(r => [r.metric, r])),
    [overall],
  );

  const bucketResults = useMemo(() =>
    BUCKETS.map(b => ({ ...b, ...computeBucketScore(b, overallMap, campaign) })),
    [overallMap, campaign],
  );

  const overallScore = useMemo(() => {
    const available = bucketResults.filter(b => b.score != null);
    if (!available.length) return null;
    return Math.round(available.reduce((s, b) => s + b.score, 0) / available.length);
  }, [bucketResults]);

  const overallGrade = getLetterGrade(overallScore);

  return (
    <div style={{ marginBottom: 24 }}>
      <Row gutter={[12, 12]}>
        {bucketResults.map((bucket) => {
          const grade = getLetterGrade(bucket.score);
          return (
            <Col key={bucket.key} xs={24} sm={12} lg={5}>
              <BucketCard bucket={bucket} grade={grade} />
            </Col>
          );
        })}

        <Col xs={24} sm={12} lg={4}>
          <Card
            size="small"
            style={{ height: '100%', borderColor: '#1677ff' }}
            styles={{ body: { padding: '12px 16px' } }}
          >
            <Text strong style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: '#1677ff' }}>
              Overall Grade
            </Text>
            <GradeDisplay score={overallScore} grade={overallGrade} />
            <Divider style={{ margin: '6px 0' }} />
            {bucketResults.map(b => (
              <MetricRow key={b.key} label={b.label} value={ordinalSuffix(b.score)} />
            ))}
          </Card>
        </Col>
      </Row>
    </div>
  );
}

function BucketCard({ bucket, grade }) {
  return (
    <Card
      size="small"
      style={{ height: '100%' }}
      styles={{ body: { padding: '12px 16px' } }}
    >
      <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {bucket.label}
      </Text>
      <GradeDisplay score={bucket.score} grade={grade} />
      <Divider style={{ margin: '6px 0' }} />
      {bucket.metrics.map(m => (
        <MetricRow key={m.key} label={m.label} rank={m.rank} n={m.n} percentile={m.percentile} />
      ))}
    </Card>
  );
}

function GradeDisplay({ score, grade }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6, marginBottom: 8 }}>
      <Tag
        color={grade?.color ?? 'default'}
        style={{ fontSize: 22, fontWeight: 700, padding: '2px 12px', margin: 0, lineHeight: '32px' }}
      >
        {grade?.letter ?? 'N/A'}
      </Tag>
      <Text style={{ fontSize: 15, fontWeight: 600, color: 'inherit' }}>
        {score != null ? `${ordinalSuffix(score)} pctl` : '—'}
      </Text>
    </div>
  );
}

function MetricRow({ label, value, rank, n, percentile }) {
  let display;
  if (value !== undefined) {
    display = value ?? '—';
  } else if (rank != null && n != null && percentile != null) {
    display = `${ordinalSuffix(rank)} / ${n} (${ordinalSuffix(percentile)} pctl)`;
  } else if (rank != null && n != null) {
    display = `${ordinalSuffix(rank)} / ${n}`;
  } else if (percentile != null) {
    display = `${ordinalSuffix(percentile)} pctl`;
  } else {
    display = '—';
  }
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginTop: 3 }}>
      <Text type="secondary" style={{ fontSize: 11 }}>{label}</Text>
      <Text style={{ fontSize: 11 }}>{display}</Text>
    </div>
  );
}
